import React, { useEffect, useState } from 'react';
import styles from './view-rating-assessment.module.css';
import { Button, CheckIcon, Chip, EditIcon, formatDate, Modal, PageHeader } from '@keplerco/core';
import Accordion from '../../../../components/accordion/accordion';
import { EmptyState } from '../../../../components/general/empty-state/empty-state';
import { useKeplerNavigate } from '../../../../navigation/guards/use-kepler-navigate';
import { PagePath } from '../../../../navigation/navigation.enums';
import { useAppActions, useAppState } from '../../../../overmind';
import { WidgetCard } from '../../../analytics/analytics-filters/widget-card';
import { StepIds } from '../rating-assessment.models';
import ratingAssessmentStyles from '../rating-assessment.module.css';
import assessmentsWizardStyles from '../../assessments-wizard.module.css';
import { ConfirmationModalLayout } from '../../../../widgets/layouts';
import { AssessmentResponse, PeerAssessmentAssigneeResponse, PeerAssessmentResponse } from '../../../../overmind/actions';
import { useParams } from 'react-router';
import { AssessmentStatusChip } from '../../../../components/chips/assessment-status.chip';
import { SkeletonOverlay } from '../../../../components/general/loading-state/skeleton-overlay';
import { ReadOnlyField } from '../../../../components/wizard/read-only-field';
import { NotificationPriority, NotificationType } from '../../../../notifications/notifications.models';
import { AssessmentStatus } from '../../../../enums/assessment-status';

function SubjectAccordion({ assignee }: { assignee: PeerAssessmentAssigneeResponse }) {
  return (
    <Accordion
      header={
        <div className={ratingAssessmentStyles.accordionHeader}>
          <div>
            <h5>{assignee.fullName}</h5>
            <div style={{ color: 'var(--text_1)' }}>
              {assignee.department ?? 'No department'} | {assignee.team ?? 'No team'}
            </div>
          </div>

          <div style={{ color: 'var(--text_1)' }}>{assignee.roles?.map(role => role.name).join(', ') ?? 'No job title'}</div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Chip customTheme={{ backgroundColor: 'white', borderColor: 'lilac', color: 'text' }}>
              {assignee.reviewers.length} {assignee.reviewers.length === 1 ? 'assessor' : 'assessors'}
            </Chip>

            <Chip customTheme={{ backgroundColor: 'white', borderColor: 'baby-blue', color: 'text' }}>
              {assignee.companySkills.length} {assignee.companySkills.length === 1 ? 'skill' : 'skills'}
            </Chip>
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h6>Skills</h6>
          </div>

          <div>
            {!!assignee.companySkills.length ? (
              assignee.companySkills.map(companySkill => (
                <div key={companySkill.name} className={styles.row} style={{ gridTemplateColumns: '1fr' }}>
                  {companySkill.name}
                </div>
              ))
            ) : (
              <EmptyState subtitle="No skills have been added yet" />
            )}
          </div>
        </div>
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h6>Assessors</h6>
          </div>

          <div>
            {!!assignee.reviewers.length ? (
              assignee.reviewers.map(reviewer => (
                <div key={reviewer.learnerSlug} className={styles.row}>
                  <div>{reviewer.fullName}</div>

                  <div>
                    {reviewer.isManager && <Chip customTheme={{ backgroundColor: 'white', borderColor: 'lilac', color: 'text' }}>Manager</Chip>}
                    {reviewer.learnerSlug === assignee.learnerSlug && <Chip customTheme={{ backgroundColor: 'white', borderColor: 'baby-blue', color: 'text' }}>Self</Chip>}
                  </div>
                </div>
              ))
            ) : (
              <EmptyState subtitle="No assessors have been added yet" />
            )}
          </div>
        </div>
      </div>
    </Accordion>
  );
}

export function ViewRatingAssessmentPage(): React.ReactElement {
  const keplerNavigate = useKeplerNavigate();

  const params = useParams();
  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [peerAssessment, setPeerAssessment] = useState<PeerAssessmentResponse>();
  const [assessmentDetails, setAssessmentDetails] = useState<AssessmentResponse>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    async function getData() {
      if (!params.assessmentSlug) return;
      setIsLoading(true);
      const assessmentDetails = await actions.getAssessment(params.assessmentSlug);
      setAssessmentDetails(assessmentDetails);

      const peerAssessment = await actions.getPeerAssessment({ assessmentSlug: params.assessmentSlug });
      setPeerAssessment(peerAssessment);
      setIsLoading(false);
    }
    getData();
  }, [params.assessmentSlug]);

  return (
    <div className="pageWrapper wrapper">
      <PageHeader
        breadcrumbs={[
          {
            name: 'Assessment Management',
            url: `${PagePath.assessmentManagementBase}${PagePath.assessmentManagement.replace(':companySlug', companyVariables.slug!)}`,
          },
          {
            name: assessmentDetails?.name || 'Rating assessment',
            url: location.pathname,
          },
        ]}
        title={assessmentDetails?.name || 'Rating assessment'}
        subtitle="Only specific fields can be edited while the assessment is running"
      />
      <SkeletonOverlay isLoading={isLoading} width="100%">
        <div className="card" style={{ marginTop: 30 }}>
          <div className={assessmentsWizardStyles.stepContainer}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <h2>Overview</h2>
              {assessmentDetails?.assessmentStatus !== AssessmentStatus.Closed && assessmentDetails?.assessmentStatus !== AssessmentStatus.Archived && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button
                    fullWidthMobile
                    type="button"
                    square
                    theme="background"
                    onClick={() => keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagementEditActiveRatingAssessment.replace(':companySlug', companyVariables.slug!).replace(':assessmentSlug', params.assessmentSlug!)}`)}
                  >
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <EditIcon tone="text" />
                      Edit
                    </div>
                  </Button>
                  <Button fullWidthMobile type="button" square theme="background" onClick={() => setIsOpen(true)}>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <CheckIcon tone="text" />
                      Close Assessment
                    </div>
                  </Button>
                </div>
              )}
            </div>

            <WidgetCard title={StepIds.Details}>
              <ReadOnlyField name="Name">{assessmentDetails?.name}</ReadOnlyField>
              <ReadOnlyField name="Description">{assessmentDetails?.description}</ReadOnlyField>
              <ReadOnlyField name="Status">
                <AssessmentStatusChip assessmentStatus={assessmentDetails?.assessmentStatus} />
              </ReadOnlyField>
            </WidgetCard>

            <WidgetCard title={StepIds.Settings}>
              <ReadOnlyField name="Skills feedback">{!!peerAssessment?.isSkillsFeedbackEnabled ? 'Assessors can add comments on the skills that they rate' : ' Skills feedback will not be collected'}</ReadOnlyField>

              <ReadOnlyField name="Assessment feedback">{!!peerAssessment?.isAssessmentFeedbackEnabled ? 'Assessors can capture overall feedback for each subject' : 'Assessment feedback will not be collected'}</ReadOnlyField>

              <ReadOnlyField name="Anonymise feedback">{!!peerAssessment?.isAnonymous ? 'Feedback and scores given will be anonymous' : 'Assessors names will be shown with the scores and feedback they have given'}</ReadOnlyField>

              <ReadOnlyField name="Manager weighting">{!!peerAssessment?.isWeightingEnabled ? 'A weighting is applied to manager ratings' : 'Manager ratings will not be given preference'}</ReadOnlyField>

              <ReadOnlyField name="Completion rules">{!!assessmentDetails?.forceAssessment ? 'Participants will be forced to complete the assessment when they access the platform' : 'Participants can complete the assessment when they choose to'}</ReadOnlyField>

              <ReadOnlyField name="Schedule">
                Start: {!!assessmentDetails?.startDate ? formatDate(new Date(assessmentDetails?.startDate)) : 'Immediately'} | End: {!!assessmentDetails?.endDate ? formatDate(new Date(assessmentDetails?.endDate)) : 'Close manually'}
              </ReadOnlyField>
            </WidgetCard>

            <WidgetCard title={StepIds.Subjects}>
              {peerAssessment?.assignees.map(assignee => (
                <SubjectAccordion key={assignee.learnerSlug} assignee={assignee} />
              ))}
            </WidgetCard>
          </div>
        </div>
      </SkeletonOverlay>

      <Modal open={isOpen} onClose={() => setIsOpen(false)}>
        <ConfirmationModalLayout
          title="You're about to close this assessment"
          subtitle="Once closed, all participants will receive their scores. Anyone who hasn’t started or is still in progress will no longer be able to complete the assessment."
          cancelButtonText="No, take me back"
          onClickCancel={() => setIsOpen(false)}
          submitButtonText="Yes, close"
          onClickSubmit={async () => {
            if (!assessmentDetails?.slug) return;
            await actions.completeAssessment(assessmentDetails.slug);
            actions.addNotification({
              title: 'Assessment closed',
              message: `${assessmentDetails.name} has been closed successfully`,
              active: true,
              id: crypto.randomUUID(),
              type: NotificationType.Success,
              priority: NotificationPriority.Toast,
              toastIncludeIcon: true,
            });
            keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagement.replace(':companySlug', companyVariables.slug!)}`);
            setIsOpen(false);
          }}
        />
      </Modal>
    </div>
  );
}
